import React, { useEffect } from 'react'

import { ApolloProvider } from '@apollo/client'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { t } from 'src/translations'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import PageWrapper from 'public/components/default_template/PageWrapper'
import CheckoutForm from 'public/components/default_template/online_ordering/checkout/CheckoutForm'
import { getTargetSegmentName } from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'
import {
  CartContextProvider,
  useCart
} from 'public/components/online_ordering/CartContext'
import {
  CheckoutContextProvider,
  useCheckout
} from 'public/components/online_ordering/CheckoutContext'
import {
  DeliveryContextProvider,
  useDelivery
} from 'public/components/online_ordering/DeliveryContext'
import { FulfillmentContextProvider } from 'public/components/online_ordering/FulfillmentContext'
import { GiftCardContextProvider } from 'public/components/online_ordering/GiftCardContext'
import {
  LoyaltyContextProvider,
  useLoyalty
} from 'public/components/online_ordering/LoyaltyContext'
import { MarketingContextProvider } from 'public/components/online_ordering/MarketingContext'
import OffersContextProvider, {
  useOffers
} from 'public/components/online_ordering/OffersContext'
import { PaymentContextProvider } from 'public/components/online_ordering/PaymentContext'
import { TimeBasedRuleContextProvider } from 'public/components/online_ordering/TimeBasedRuleContext'
import { getGlobalNavOverrideForOO } from 'public/components/online_ordering/globalNavUtils'

import { DebugPanel } from './debug/DebugPanel'
import { getFirstOrThirdParty } from './online_ordering/checkout/loyalty/LoyaltyUtils'
import { UpsellContextProvider } from '../online_ordering/UpsellContext'
import { PopoverContextProvider } from 'src/shared/components/common/popover/PopoverContext'
import { CART_POPOVER_CONTEXT_KEY } from './online_ordering/cart/CartModal'
import { A11yProvider } from '../online_ordering/useA11y'

export const CheckoutPageContent = ({
  nav,
  footer
}: {
  nav: React.ReactNode
  footer?: React.ReactNode
}) => {
  const { cart } = useCart()
  const { track } = useThrottledTracker()
  const { pointsBalance, redemptions } = useLoyalty()
  const { ooRestaurant } = useRestaurant()
  const { enabledPaymentOptions } = useCheckout()
  const { achievedOffers, rankedPromoOfferDiscounts } = useOffers()
  const { savedAddressUsed } = useDelivery()
  const { cartGuid, loadingCart } = useCart()

  useEffect(() => {
    if (typeof window !== 'undefined' && ooRestaurant) {
      const baseData = {
        cartGuid: cartGuid,
        firstOrThirdParty: getFirstOrThirdParty(
          ooRestaurant.loyaltyConfig?.programName
        ),
        pointsBalance: pointsBalance,
        numOfRedemptions: redemptions.length,
        numOffersAchieved: achievedOffers?.length ?? 0,
        enabledPaymentOptions: Array.from(enabledPaymentOptions.values()),
        numItemsInCart: 0
      }
      if (!loadingCart) {
        if (cart) {
          const numAvailableRedemptions = redemptions.reduce(
            (acc, r) => acc + (r.available ? 1 : 0),
            0
          )
          const appliedPromoCode =
            cart?.order?.discounts?.restaurantDiscount?.promoCode ?? null
          const targetSegment = getTargetSegmentName(
            appliedPromoCode,
            rankedPromoOfferDiscounts
          )
          track(TrackingEvents.CHECKOUT_PAGE_VIEW, {
            ...baseData,
            cartSource: cart?.cartSource,
            diningOption: cart.diningOptionBehavior,
            deliveryProvider: cart.deliveryProviderInfo?.provider,
            savedAddressUsed:
              cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
                ? savedAddressUsed
                : undefined,
            numAvailableRedemptions,
            isDeliveryAndNoDeliveryInfo:
              cart?.diningOptionBehavior === DiningOptionBehavior.Delivery &&
              !cart?.order?.deliveryInfo,
            validDeliveryAddressPresent: !!cart?.order?.deliveryInfo,
            numItemsInCart: cart?.order?.numberOfSelections ?? 0,
            targetSegment
          })
        } else {
          track(TrackingEvents.CHECKOUT_PAGE_VIEW, baseData)
        }
      }
    }
    // run once when all the data has finished loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cart,
    track,
    ooRestaurant,
    pointsBalance,
    redemptions.length,
    loadingCart
  ])

  return (
    <div
      className='defaultTemplate whiteBackground checkoutPage'
      data-testid='checkoutPage'
    >
      {nav}
      <div className='paddedSection'>
        <div
          className='checkout'
          role='main'
          id='main'
          aria-label={t('checkout-page.title')}
        >
          <CheckoutForm />
          <DebugPanel />
        </div>
      </div>
      {footer}
    </div>
  )
}

const RestaurantCheckoutPage = () => {
  const { restaurant } = useRestaurant()
  const navOverride = getGlobalNavOverrideForOO(restaurant)
  return (
    <PageWrapper
      Component={CheckoutPageContent}
      shouldShowPreviewBanner={false}
      navTypeOverride={navOverride}
    />
  )
}

export const ContextualCheckoutPage = ({
  children
}: React.PropsWithChildren<{}>) => {
  const ooClient = useOOClient()

  return (
    <ApolloProvider client={ooClient}>
      <A11yProvider>
        <CartContextProvider>
          <DeliveryContextProvider>
            <FulfillmentContextProvider>
              <TimeBasedRuleContextProvider>
                <GiftCardContextProvider>
                  <PaymentContextProvider>
                    <MarketingContextProvider>
                      <CheckoutContextProvider>
                        <LoyaltyContextProvider>
                          <OffersContextProvider>
                            <UpsellContextProvider>
                              <PopoverContextProvider
                                contextKey={CART_POPOVER_CONTEXT_KEY}
                              >
                                {children}
                              </PopoverContextProvider>
                            </UpsellContextProvider>
                          </OffersContextProvider>
                        </LoyaltyContextProvider>
                      </CheckoutContextProvider>
                    </MarketingContextProvider>
                  </PaymentContextProvider>
                </GiftCardContextProvider>
              </TimeBasedRuleContextProvider>
            </FulfillmentContextProvider>
          </DeliveryContextProvider>
        </CartContextProvider>
      </A11yProvider>
    </ApolloProvider>
  )
}

const CheckoutPage = () => (
  <ContextualCheckoutPage>
    <RestaurantCheckoutPage />
  </ContextualCheckoutPage>
)
export default CheckoutPage
